import { useCallback, useMemo } from 'react';
import { useTranslation } from '../LanguageManagement/hooks/useTranslation';

interface UseMasterCrudTranslationProps {
  locale?: string;
  uiPrefix?: string;
  fieldsPrefix?: string;
  externalTranslations?: Record<string, string>;
  internalTranslations: Record<string, any>;
  tProp?: (key: string, defaultValue?: string) => string;
}

/**
 * Hook interno para gestionar la traducción en MasterCrud con retrocompatibilidad y agnosticismo.
 * 
 * Prioridad de resolución:
 * 1. tProp (Función inyectada por el usuario - Agnosticismo Total)
 * 2. externalTranslations (Mapeo estático por props)
 * 3. Sistema global del proyecto (LanguageManagement / Contexto)
 * 4. Diccionario interno estático (Fallback de seguridad)
 */
export const useMasterCrudTranslation = ({
  locale = 'es',
  uiPrefix = 'mastercrud',
  fieldsPrefix,
  externalTranslations,
  internalTranslations,
  tProp,
}: UseMasterCrudTranslationProps) => {
  let globalI18n: any = null;
  try {
    // Intentar usar el sistema del proyecto si no se provee una función externa
    if (!tProp) {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      globalI18n = useTranslation();
    }
  } catch (e) {
    globalI18n = null;
  }

  const { t: globalT, isReady } = globalI18n || {};
  const activeT = tProp || globalT;

  const t = useCallback((key: string, defaultValue?: string): string => {
    // 1. Prioridad: Función inyectada o Global
    if (activeT && (tProp || isReady)) {
      // Intentar resolver con prefijo de UI
      const uiKey = `${uiPrefix}.${key}`;
      const translated = activeT(uiKey, '__NOT_FOUND__');
      
      if (translated !== '__NOT_FOUND__' && translated !== uiKey) {
        return translated;
      }

      // Si no se encontró y es una llave de campo, intentar con prefijo de campos
      if (fieldsPrefix) {
        const fieldKey = `${fieldsPrefix}.${key}`;
        const fieldTranslated = activeT(fieldKey, '__NOT_FOUND__');
        if (fieldTranslated !== '__NOT_FOUND__' && fieldTranslated !== fieldKey) {
          return fieldTranslated;
        }
      }
    }

    // 2. Prioridad: Diccionario estático externo
    if (externalTranslations && externalTranslations[key]) {
      return externalTranslations[key];
    }

    // 3. Prioridad: Diccionario interno estático
    const internalSet = internalTranslations[locale] || internalTranslations.es;
    if (internalSet && internalSet[key]) {
      return internalSet[key];
    }

    // 4. Fallback final
    return defaultValue || key;
  }, [activeT, tProp, isReady, uiPrefix, fieldsPrefix, externalTranslations, internalTranslations, locale]);

  return { t, isReady: isReady ?? true };
};
